import Head from "next/head";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useCart } from "@context/CartContext";
import BrochureLeadModal from "@/components/common/BrochureLeadModal";

export default function ODCWorkshops() {
  const { addToCart, setShowCart } = useCart();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);

  const coreCapabilities = [
    "Communication capabilities",
    "Interpersonal effectiveness",
    "Emotional intelligence",
    "Behavioural competencies",
    "Professional attitudes",
  ];

  const learningExperience = [
    "Navigate complex workplace environments",
    "Collaborate effectively across teams",
    "Build stronger professional relationships",
    "Deliver superior customer experiences",
  ];

  useEffect(() => {
    fetchProgramData();
  }, []);

  const fetchProgramData = async () => {
    try {
      // Fetch program details
      const programResponse = await fetch("/api/programs/odc-workshops");
      const programResult = await programResponse.json();

      if (programResult.success) {
        setProgram({
          ...programResult.data,
          badge: "Online",
          description:
            "High-impact, shorter-format soft skills workshops for teams and professionals who need focused development without a full multi-week commitment.",
        });

        // Fetch schedules for this program
        const schedulesResponse = await fetch("/api/schedules");
        const schedulesResult = await schedulesResponse.json();

        if (schedulesResult.success) {
          const programSchedules = schedulesResult.data.filter(
            (s) => s.programSlug === "odc-workshops",
          );
          setSchedules(programSchedules);
        }
      } else {
        setError("Failed to load program information");
      }
    } catch (err) {
      console.error("Error fetching program data:", err);
      setError("Failed to load program information. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (format, schedule = null) => {
    if (!program) return;

    addToCart({
      programId: program._id,
      formatId: format.id,
      title: schedule ? `${program.title} - ${schedule.title}` : `${program.title} - ${format.name}`,
      price: format.price || 0,
      featuredImg: program.featuredImg,
      date: schedule ? schedule.date : null,
      location: schedule ? schedule.location : null
    });

    // Open the cart instead of redirecting
    setShowCart(true);
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || "Program not found"}</p>
          <Link
            href="/programs"
            className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md"
          >
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{program.title} | L.I.S.T Learning Pathways</title>
        <meta
          name="description"
          content={
            program.description ||
            "Niche, high-impact workshops focusing on specific soft skills challenges."
          }
        />
      </Head>

      <main className="min-h-screen bg-white">
        {/* Hero */}
        <section className="relative overflow-hidden bg-brand-dark pt-12 pb-12 lg:pt-28 lg:pb-20">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                {program.badge && (
                  <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-base font-bold rounded-full mb-4">
                    {program.badge}
                  </span>
                )}
                <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                  ODC <span className="text-brand-primary">Workshops</span>
                </h1>
                <p className="text-lg text-gray-300 max-w-xl leading-relaxed mb-8">
                  {program.description}
                </p>
                <button
                  type="button"
                  onClick={() => setIsBrochureModalOpen(true)}
                  className="inline-block px-8 py-4 bg-white border-2 border-white text-brand-primary font-bold rounded-md hover:bg-brand-primary hover:border-brand-primary hover:text-white transition-all active:scale-95 text-center"
                >
                  Download Brochure
                </button>
              </div>
              <div className="mt-0 lg:mt-0">
                <div className="aspect-[4/3] relative rounded-lg overflow-hidden shadow-xl group">
                  <img
                    src={
                      program.featuredImg ||
                      "/program/p-3.jpg"
                    }
                    alt={program.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-brand-dark/20 group-hover:bg-brand-dark/10 transition-colors"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing & Formats */}
        {program.formats && program.formats.length > 0 && (
          <section id="enroll" className="py-16 lg:py-20 bg-gray-50/50">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-base font-bold rounded-full mb-3">
                  Workshop Formats
                </span>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">
                  Available Workshops
                </h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {program.formats.map((format, index) => (
                  <div
                    key={format.id}
                    className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                  >
                    <h4 className="text-lg font-bold text-brand-dark mb-2">
                      {format.name}
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-sm font-bold text-brand-primary bg-brand-primary/5 px-2 py-0.5 rounded-full">
                        {format.duration}
                      </span>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm text-gray-500 block">
                        Tuition
                      </span>
                      <span className="text-2xl font-black text-brand-dark">
                        {format.price > 0 ? `₹${format.price.toLocaleString()}` : "Price Upon Request"}
                      </span>
                      <p className="text-[10px] text-gray-400 font-medium mt-1">(Inclusive of 18% GST)</p>
                    </div>
                    {format.price > 0 && (
                      <button
                        onClick={() => handleAddToCart(format)}
                        className="block w-full text-center py-2.5 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95 text-sm"
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Upcoming Sessions */}
        {schedules.length > 0 && (
          <section className="py-16 lg:py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 bg-brand-primary text-white text-base font-bold rounded-full mb-3">
                  Upcoming Events
                </span>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">
                  Workshop Schedule
                </h2>
              </div>

              <div className="max-w-4xl mx-auto space-y-4">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="flex flex-col md:flex-row justify-between items-center p-6 bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-xl hover:border-brand-primary/20 transition-all duration-300 group"
                  >
                    <div className="flex items-center gap-4 mb-4 md:mb-0">
                      <div>
                        <h4 className="text-lg font-bold text-brand-dark group-hover:text-brand-primary transition-colors">
                          {schedule.title}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-sm font-bold text-brand-primary bg-brand-primary/5 px-2 py-0.5 rounded-full">
                            {schedule.formatName}
                          </span>
                          <span className="text-sm font-bold text-gray-300 bg-gray-50 px-2 py-0.5 rounded-full">
                            {schedule.location}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row items-center gap-4">
                      <div className="text-center md:text-right">
                        <span className="block text-lg font-bold text-brand-dark">
                          {schedule.dateStr}
                        </span>
                        <span className="text-sm text-gray-300">
                          {schedule.time}
                        </span>
                      </div>
                      {schedule.price > 0 && (
                        <button
                          onClick={() => handleAddToCart(program.formats.find(f => f.id === schedule.formatId), schedule)}
                          className="px-6 py-3 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95 text-sm whitespace-nowrap"
                        >
                          Add to Cart
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12 max-w-4xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight mb-4">
                Curriculum Focus
              </h2>
              <p className="text-base lg:text-lg text-gray-500 leading-relaxed">
                L.I.S.T&apos;s one-day comprehensive, three-hour workshop helps
                organisations develop the human skills that strengthen business
                performance. While technical expertise remains vital,
                communication, emotional intelligence, collaboration, and
                adaptability are often what enable teams to realise their full
                potential.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="bg-brand-dark text-white rounded-2xl p-8 lg:p-10 shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-brand-primary/10"></div>
                <div className="relative z-10">
                  <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-[0.18em] rounded-full mb-4">
                    One-Day Comprehensive
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-extrabold tracking-tight mb-4">
                    Human skills that directly support workplace performance
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    This workshop is designed for organisations that want a
                    concise, practical intervention focused on the people skills
                    that improve collaboration, service, and day-to-day
                    effectiveness.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {coreCapabilities.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/10 text-sm text-white"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-6">
                <div className="bg-white p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-brand-primary mb-3">
                    Workshop Focus
                  </span>
                  <h4 className="text-xl font-bold text-brand-dark mb-3">
                    Practical, relevant, and immediately usable
                  </h4>
                  <p className="text-gray-500 leading-relaxed">
                    Participants work through real workplace soft skills and
                    collaboration challenges in a format that is short, focused,
                    and designed for immediate application back on the job.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 lg:p-8 rounded-2xl border border-gray-100 shadow-sm">
                  <span className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-brand-primary mb-3">
                    Learning Experience
                  </span>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {learningExperience.map((item, index) => (
                      <li
                        key={index}
                        className="bg-white rounded-xl px-4 py-3 text-sm text-gray-600 border border-gray-100"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Custom Workshop Request */}
        <section className="pb-16 lg:pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-brand-dark rounded-lg p-8 lg:p-12 text-center text-white relative overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-brand-primary/10"></div>
              <div className="relative z-10 max-w-2xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-extrabold mb-4 tracking-tight">
                  Need a <span className="text-brand-primary">Specific</span>{" "}
                  Theme?
                </h3>
                <p className="text-base text-gray-300 mb-8">
                  We can design a bespoke ODC 3-Hour Workshop for your specific
                  soft skills challenges. From retail excellence to crisis
                  management.
                </p>
                <Link
                  href="/contact"
                  className="inline-block px-8 py-4 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95"
                >
                  Request Bespoke Topic
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <BrochureLeadModal
        isOpen={isBrochureModalOpen}
        onClose={() => setIsBrochureModalOpen(false)}
        brochureUrl="/brochure/LIST%20ODC.pdf"
        programId={program?._id}
        programName={program?.title || "ODC Workshops"}
        format={program?.formats?.[0]?.name || "ODC Workshop"}
      />
    </>
  );
}
